import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/shared/Button';
import Icon from '../components/shared/Icon';
import Skeleton from '../components/shared/Skeleton';
import { MOCK_WORKOUTS } from '../utils/mockData';
import './SchedulePage.css';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getCalendarDays(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const days = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

function buildWorkoutsByDate(workouts) {
  const map = {};
  workouts.forEach((w) => {
    if (!map[w.scheduledDate]) map[w.scheduledDate] = [];
    map[w.scheduledDate].push(w);
  });
  return map;
}

function WorkoutPopup({ workout, onClose, onView, onAddWorkout }) {
  if (!workout) return null;
  return (
    <div className="schedule__popup-backdrop" onClick={onClose}>
      <div className="schedule__popup" onClick={(e) => e.stopPropagation()}>
        <div className="schedule__popup-header">
          <span className="schedule__popup-name">{workout.name}</span>
          <button className="schedule__popup-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="schedule__popup-meta">
          <span>{workout.exercises.length} exercises</span>
          <span>·</span>
          <span>{workout.duration} min</span>
          <span>·</span>
          <span>{workout.workOutputTarget.toLocaleString()} WU</span>
        </div>
        {workout.tags && workout.tags.length > 0 && (
          <div className="schedule__popup-tags">
            {workout.tags.map((t) => (
              <span key={t} className="schedule__popup-tag">{t}</span>
            ))}
          </div>
        )}
        {workout.description && (
          <p className="schedule__popup-desc">{workout.description}</p>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => onView(workout.id)}
          className="schedule__popup-btn"
        >
          View Workout
        </Button>
        {onAddWorkout && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddWorkout(workout.scheduledDate)}
            className="schedule__popup-btn"
          >
            + Add Workout
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const today = new Date();
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const calDays = getCalendarDays(year, month);
  const workoutsByDate = buildWorkoutsByDate(MOCK_WORKOUTS);
  const todayStr = today.toISOString().slice(0, 10);

  function prevMonth() {
    setViewDate(new Date(year, month - 1, 1));
  }

  function nextMonth() {
    setViewDate(new Date(year, month + 1, 1));
  }

  function dayStr(day) {
    if (!day) return null;
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  }

  if (loading) {
    return (
      <div className="schedule-page">
        <div className="schedule__nav">
          <Skeleton width={36} height={36} rounded />
          <Skeleton width={160} height={24} />
          <Skeleton width={36} height={36} rounded />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginTop: 'var(--space-4)' }}>
          {[...Array(35)].map((_, i) => <Skeleton key={i} height={68} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-page">
      <div className="schedule__nav">
        <button className="schedule__nav-btn" onClick={prevMonth} aria-label="Previous month">
          <Icon name="back" size={18} />
        </button>
        <h1 className="schedule__month-title">{MONTH_NAMES[month]} {year}</h1>
        <button className="schedule__nav-btn" onClick={nextMonth} aria-label="Next month">
          <Icon name="forward" size={18} />
        </button>
      </div>

      <div className="schedule__calendar">
        {WEEKDAY_LABELS.map((d) => (
          <div key={d} className="schedule__day-header">{d}</div>
        ))}
        {calDays.map((day, idx) => {
          const ds = dayStr(day);
          const workouts = ds ? (workoutsByDate[ds] || []) : [];
          const isToday = ds === todayStr;
          const isPast = ds && ds < todayStr;
          return (
            <div
              key={idx}
              className={[
                'schedule__day',
                !day ? 'schedule__day--empty' : '',
                isToday ? 'schedule__day--today' : '',
                isPast ? 'schedule__day--past' : '',
                workouts.length > 0 ? 'schedule__day--has-workouts' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => workouts.length > 0 && setSelectedWorkout(workouts[0])}
            >
              {day && (
                <>
                  <span className="schedule__day-number">{day}</span>
                  <div className="schedule__day-pills">
                    {workouts.slice(0, 2).map((w) => (
                      <span key={w.id} className="schedule__workout-pill">{w.name}</span>
                    ))}
                    {workouts.length > 2 && (
                      <span className="schedule__workout-pill schedule__workout-pill--more">
                        +{workouts.length - 2} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <WorkoutPopup
        workout={selectedWorkout}
        onClose={() => setSelectedWorkout(null)}
        onView={(id) => navigate(`/app/workouts/${id}`)}
        onAddWorkout={(date) => navigate(`/app/workout/new?date=${date}`)}
      />
    </div>
  );
}
